import { FC, ReactNode } from 'react';

import Loader from '../../../../../components/loader/index';
import { ScenarioHooks, useLocale } from '../../../../../hooks/index';
import { CapTablePool } from '../../../../../hooks/scenario-modeling/index';
import { StringKey } from '../../../../../lang/index';
import { stakeholderTypeTranslation } from '../../../../../translations/stakeholder-translation.util';
import { StakeholderType } from '../../../../../types/stakeholder.types';
import { toRound } from '../../../../../utils/get-rounded-number.util';
import {
  getScenarioFundraisingCommonStockTotal,
  getScenarioPoolUnvestedShareClassTotals,
  mapScenarioPoolShareClassAmounts,
} from '../../../../../utils/pool-share-class-columns.util';
import { calcTotal } from '../../../../../utils/reduce-utils.util';
import { getScenarioGrantedPoolFigures } from '../../../../../utils/scenario-granted-pool-row.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { toUpperCaseWords } from '../../../../../utils/to-uppercase-util.util';
import { CapTableRowValue, CapTableTab, Column, StakeholdersViewBy } from '../fundraising.types';
import { EquityPoolsRow, TabProps, TotalRow } from '../table-components/index';
import { StakeholderTableItem } from './stakeholder-item';

type SelectedTabProps = {
  companyPools: CapTablePool[];
  defaultShareClassId: string;
  messagesLocale: Record<StringKey, string>;
  modifiedShareClassesIds?: string[];
  scenarioId: string;
  scenarioPools: CapTablePool[];
  shareClasses: ({
    id: string;
    name: string;
  } & CapTableRowValue)[];
  shareClassesFromCompanyPools: ({
    id: string;
    name: string;
  } & CapTableRowValue)[];
  shareClassesFromScenarioPools: ({
    id: string;
    name: string;
  } & CapTableRowValue)[];
} & Omit<TabProps, 'companyId'>;

export const stakeholdersCapTableViewBy: Record<
  'all' | StakeholdersViewBy,
  (props: SelectedTabProps) => ReactNode
> = {
  [StakeholdersViewBy.STAKEHOLDERS]: (props) => <StakeholdersItemTab {...props} />,
  [StakeholdersViewBy.TYPE]: (props) => <StakeholdersTypeTab {...props} />,
  all: (props) => <StakeholdersAllTab {...props} />,
};

export const StakeholdersTab: FC<TabProps> = ({
  visibleColumns,
  selectedViewBy,
  viewBy,
  scenarioId,
}) => {
  const { data: capTableShareClasses = [], isLoading: isLoadingShareClasses } =
    ScenarioHooks.useCapTableShareClasses();
  const { modifiedShareClasses } = ScenarioHooks.useModifiedShareClasses({ scenarioId });
  const modifiedShareClassesIds =
    modifiedShareClasses?.map(({ shareClass }) => shareClass.id) || [];

  const { messagesLocale } = useLocale();

  const { data: summarizeData, isLoading } = ScenarioHooks.useCapTableSummarize();
  const isCapTableLoading = Boolean(!summarizeData || isLoading || isLoadingShareClasses);
  const {
    avgSharePrice,
    convertedBasis,
    convertedBasisPercentage,
    diluted,
    dilutedPercentage,
    investment,
    issued,
    issuedPercentage,
    votingPercentage,
    voting,
    companyPools = [],
    scenarioPools = [],
    equityPools = [],
  } = summarizeData || {};

  const shareClassesFromCompanyPools = companyPools
    .reduce(
      (acc, { shareClasses }) => acc.concat(shareClasses),
      [] as ({ id: string; name: string } & CapTableRowValue)[],
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  const shareClassesFromScenarioPools = scenarioPools
    .reduce(
      (acc, { shareClasses }) => acc.concat(shareClasses),
      [] as ({ id: string; name: string } & CapTableRowValue)[],
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const shareClassesFromEquityPools = Array.from(
    new Map(
      equityPools.flatMap(({ shareClasses }) => [...shareClasses]).map((item) => [item.id, item]),
    ).values(),
  ).sort((a, b) => a.name.localeCompare(b.name));

  const defaultShareClass = capTableShareClasses?.find(({ isDefault }) => isDefault);

  return (
    <>
      {stakeholdersCapTableViewBy[selectedViewBy]({
        visibleColumns,
        selectedViewBy,
        viewBy,
        companyPools,
        scenarioPools,
        shareClasses: shareClassesFromEquityPools,
        shareClassesFromCompanyPools,
        shareClassesFromScenarioPools,
        messagesLocale,
        defaultShareClassId: defaultShareClass?.shareClass?.id || '',
        scenarioId,
        modifiedShareClassesIds,
      })}

      <TotalRow
        fields={{
          [Column.COMMON_STOCK]: {
            value: getScenarioFundraisingCommonStockTotal({
              stakeholderDefaultShareClassIssued: toNumber(defaultShareClass?.issued),
              companyPools,
              scenarioPools,
              defaultShareClassId: defaultShareClass?.shareClass?.id || '',
            }),
            change: null,
          },
          [Column.OTHER_SHARE_CLASSES]: {
            value: shareClassesFromEquityPools
              .filter(({ id }) => id !== defaultShareClass?.shareClass?.id)
              ?.map(
                ({ value, id: shareClassId }) =>
                  value +
                  calcTotal(
                    capTableShareClasses.filter(({ shareClass }) => shareClass.id === shareClassId),
                    ({ issued }) => issued,
                  ),
              ),
            change: null,
          },
          [Column.TYPE]: {
            value: viewBy?.type ? messagesLocale['TOTAL'] : '',
            change: null,
          },
          [Column.NAME]: {
            change: null,
            value: messagesLocale['TOTAL'],
          },
          [Column.STAKEHOLDER_NAME]: {
            change: null,
            value: messagesLocale['TOTAL'],
          },
          [Column.ISSUED]: {
            change: null,
            value: toNumber(issuedPercentage) * 100,
          },
          [Column.DILUTED]: {
            value: toNumber(dilutedPercentage) * 100,
            change: null,
          },
          [Column.VOTING]: {
            value: toNumber(votingPercentage) * 100,
            change: null,
          },
          [Column.CONVERTED_PERCENTAGE]: {
            value: toNumber(convertedBasisPercentage) * 100,
            change: null,
          },
          [Column.NO_ISSUED]: {
            change: null,
            value: toNumber(issued),
          },
          [Column.DILUTED_SHARES]: {
            change: null,
            value: toNumber(diluted),
          },

          [Column.VOTING_RIGHTS]: {
            change: null,
            value: toNumber(voting),
          },
          [Column.INVESTMENT]: {
            change: null,
            value: toNumber(investment),
          },
          [Column.AVERAGE_SHARE_PRICE]: {
            change: null,
            value: toNumber(avgSharePrice),
          },
          [Column.NO_OF_STAKEHOLDERS]: {
            value: 0,
            change: null,
          },

          [Column.CONVERTED_SHARES]: {
            change: null,
            value: toNumber(convertedBasis),
          },
        }}
        selectedViewBy={selectedViewBy}
        tab={CapTableTab.BY_STAKEHOLDER}
        trClassName={isCapTableLoading ? 'animate-pulse bg-gray-200/80' : ''}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
    </>
  );
};

export const StakeholdersAllTab: FC<SelectedTabProps> = (props) => {
  const { visibleColumns, selectedViewBy, messagesLocale, modifiedShareClassesIds } = props;
  const { data, isLoading } = ScenarioHooks.useCapTableStakeholderByAll();
  const newStakeholders = data?.filter(({ type }) => !type);
  const stakeholders = data?.filter(({ type }) => type);
  if (isLoading) return <Loader />;

  return (
    <>
      {stakeholders?.map(
        (
          {
            stakeholders,
            type,
            avgSharePrice,
            convertedBasis,
            convertedBasisPercentage,
            diluted,
            dilutedPercentage,
            investment,
            issued,
            issuedPercentage,
            shareClasses,
            votingPercentage,
            voting,
          },
          i,
        ) => {
          const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
          return (
            <StakeholderTableItem
              fields={{
                [Column.NAME]: {
                  value: messagesLocale[stakeholderTypeTranslation[type as StakeholderType]],
                  change: null,
                },
                [Column.STAKEHOLDER_NAME]: null,
                [Column.TYPE]: null,
                [Column.ISSUED]: {
                  value: issuedPercentage * 100,
                  change: null,
                },
                [Column.DILUTED]: {
                  value: dilutedPercentage * 100,
                  change: null,
                },
                [Column.VOTING]: {
                  value: votingPercentage * 100,
                  change: null,
                },
                [Column.CONVERTED_PERCENTAGE]: {
                  value: convertedBasisPercentage * 100,
                  change: null,
                },
                [Column.NO_ISSUED]: {
                  change: null,
                  value: issued,
                },
                [Column.DILUTED_SHARES]: {
                  change: null,
                  value: diluted,
                },
                [Column.COMMON_STOCK]: {
                  change: null,
                  value: toNumber(defaultShareClass?.issued),
                },
                [Column.OTHER_SHARE_CLASSES]: {
                  value: shareClasses
                    .filter(({ isDefault }) => !isDefault)
                    .map(({ issued }) => issued),
                  change: null,
                },
                [Column.VOTING_RIGHTS]: {
                  change: null,
                  value: voting,
                },
                [Column.INVESTMENT]: {
                  change: null,
                  value: investment,
                },
                [Column.AVERAGE_SHARE_PRICE]: {
                  change: null,
                  value: avgSharePrice,
                },
                [Column.CONVERTED_SHARES]: {
                  change: null,
                  value: convertedBasis,
                },
              }}
              items={stakeholders}
              key={`${i}_${issued}${type}`}
              modifiedShareClassesIds={modifiedShareClassesIds}
              selectedViewBy={selectedViewBy}
              visibleColumns={visibleColumns}
            />
          );
        },
      )}
      <StakeholderTabPools {...props} />
      {newStakeholders &&
        newStakeholders?.[0]?.stakeholders?.length > 0 &&
        newStakeholders?.map(
          (
            {
              type,
              avgSharePrice,
              convertedBasis,
              convertedBasisPercentage,
              diluted,
              dilutedPercentage,
              investment,
              issued,
              issuedPercentage,
              shareClasses,
              votingPercentage,
              voting,
              stakeholders,
            },
            i,
          ) => {
            const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
            return (
              <StakeholderTableItem
                fields={{
                  [Column.NAME]: {
                    value: 'New Investors',
                    change: null,
                  },
                  [Column.STAKEHOLDER_NAME]: null,
                  [Column.TYPE]: null,
                  [Column.ISSUED]: {
                    value: issuedPercentage * 100,
                    change: null,
                  },
                  [Column.DILUTED]: {
                    value: dilutedPercentage * 100,
                    change: null,
                  },
                  [Column.VOTING]: {
                    value: votingPercentage * 100,
                    change: null,
                  },
                  [Column.CONVERTED_PERCENTAGE]: {
                    value: convertedBasisPercentage * 100,
                    change: null,
                  },
                  [Column.NO_ISSUED]: {
                    change: null,
                    value: issued,
                  },
                  [Column.DILUTED_SHARES]: {
                    change: null,
                    value: diluted,
                  },
                  [Column.COMMON_STOCK]: {
                    change: null,
                    value: toNumber(defaultShareClass?.issued),
                  },
                  [Column.OTHER_SHARE_CLASSES]: {
                    value: shareClasses
                      .filter(({ isDefault }) => !isDefault)
                      .map(({ issued }) => issued),
                    change: null,
                  },
                  [Column.VOTING_RIGHTS]: {
                    change: null,
                    value: voting,
                  },
                  [Column.INVESTMENT]: {
                    change: null,
                    value: investment,
                  },
                  [Column.AVERAGE_SHARE_PRICE]: {
                    change: null,
                    value: avgSharePrice,
                  },
                  [Column.CONVERTED_SHARES]: {
                    change: null,
                    value: convertedBasis,
                  },
                }}
                items={stakeholders}
                key={`${i}_${issued}${type}`}
                modifiedShareClassesIds={modifiedShareClassesIds}
                selectedViewBy={selectedViewBy}
                trClassName="bg-sunbeam-50"
                visibleColumns={visibleColumns}
              />
            );
          },
        )}
    </>
  );
};

export const StakeholdersTypeTab: FC<SelectedTabProps> = (props) => {
  const {
    visibleColumns,
    selectedViewBy,
    messagesLocale,
    viewBy,
    scenarioId,
    modifiedShareClassesIds,
  } = props;
  const { data } = ScenarioHooks.useCapTableStakeholderTypes({ scenarioId });
  const newStakeholders = data?.filter(({ type }) => !type);
  const stakeholders = data?.filter(({ type }) => type);
  return (
    <>
      {stakeholders?.map(
        (
          {
            type,
            avgSharePrice,
            convertedBasis,
            convertedBasisPercentage,
            diluted,
            dilutedPercentage,
            investment,
            issued,
            issuedPercentage,
            shareClasses,
            votingPercentage,
            voting,
            avgSharePriceChange,
            convertedBasisChange,
            convertedBasisPercentageChange,
            dilutedChange,
            dilutedPercentageChange,
            investmentChange,
            issuedChange,
            issuedPercentageChange,
            votingChange,
            votingPercentageChange,
          },
          i,
        ) => {
          const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
          return (
            <StakeholderTableItem
              fields={{
                [Column.NAME]: null,
                [Column.STAKEHOLDER_NAME]: null,
                [Column.TYPE]: {
                  change: null,
                  value: messagesLocale[stakeholderTypeTranslation[type as StakeholderType]],
                },
                [Column.ISSUED]: {
                  value: issuedPercentage * 100,
                  change: issuedPercentageChange,
                },
                [Column.DILUTED]: {
                  value: dilutedPercentage * 100,
                  change: dilutedPercentageChange,
                },
                [Column.VOTING]: {
                  value: votingPercentage * 100,
                  change: votingPercentageChange,
                },
                [Column.CONVERTED_PERCENTAGE]: {
                  value: convertedBasisPercentage * 100,
                  change: convertedBasisPercentageChange,
                },
                [Column.NO_ISSUED]: {
                  change: issuedChange,
                  value: issued,
                },
                [Column.DILUTED_SHARES]: {
                  change: dilutedChange,
                  value: diluted,
                },
                [Column.COMMON_STOCK]: {
                  change: toNumber(defaultShareClass?.issuedChange),
                  value: toNumber(defaultShareClass?.issued),
                },
                [Column.OTHER_SHARE_CLASSES]: {
                  value: shareClasses
                    .filter(({ isDefault }) => !isDefault)
                    .map(({ issued }) => issued),
                  change: shareClasses
                    .filter(({ isDefault }) => !isDefault)
                    .map(({ issuedChange }) => issuedChange),
                },
                [Column.VOTING_RIGHTS]: {
                  change: votingChange,
                  value: voting,
                },
                [Column.INVESTMENT]: {
                  change: investmentChange,
                  value: investment,
                },
                [Column.AVERAGE_SHARE_PRICE]: {
                  change: avgSharePriceChange,
                  value: avgSharePrice,
                },
                [Column.CONVERTED_SHARES]: {
                  change: convertedBasisChange,
                  value: convertedBasis,
                },
              }}
              key={`${i}_${issued}${type}`}
              modifiedShareClassesIds={modifiedShareClassesIds}
              selectedViewBy={selectedViewBy}
              viewBy={viewBy}
              visibleColumns={visibleColumns}
            />
          );
        },
      )}
      <StakeholderTabPools {...props} />
      {newStakeholders &&
        newStakeholders?.length > 0 &&
        newStakeholders?.map(
          (
            {
              type,
              avgSharePrice,
              convertedBasis,
              convertedBasisPercentage,
              diluted,
              dilutedPercentage,
              investment,
              issued,
              issuedPercentage,
              shareClasses,
              votingPercentage,
              voting,
              avgSharePriceChange,
              convertedBasisChange,
              convertedBasisPercentageChange,
              dilutedChange,
              dilutedPercentageChange,
              investmentChange,
              issuedChange,
              issuedPercentageChange,
              votingChange,
              votingPercentageChange,
            },
            i,
          ) => {
            const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
            return (
              <StakeholderTableItem
                fields={{
                  [Column.NAME]: {
                    value: 'New Investors',
                    change: null,
                  },
                  [Column.STAKEHOLDER_NAME]: null,
                  [Column.TYPE]: {
                    value: 'New Investors',
                    change: null,
                  },
                  [Column.ISSUED]: {
                    value: issuedPercentage * 100,
                    change: issuedPercentageChange,
                  },
                  [Column.DILUTED]: {
                    value: dilutedPercentage * 100,
                    change: dilutedPercentageChange,
                  },
                  [Column.VOTING]: {
                    value: votingPercentage * 100,
                    change: votingPercentageChange,
                  },
                  [Column.CONVERTED_PERCENTAGE]: {
                    value: convertedBasisPercentage * 100,
                    change: convertedBasisPercentageChange,
                  },
                  [Column.NO_ISSUED]: {
                    change: issuedChange,
                    value: issued,
                  },
                  [Column.DILUTED_SHARES]: {
                    change: dilutedChange,
                    value: diluted,
                  },
                  [Column.COMMON_STOCK]: {
                    change: toNumber(defaultShareClass?.issuedChange),
                    value: toNumber(defaultShareClass?.issued),
                  },
                  [Column.OTHER_SHARE_CLASSES]: {
                    value: shareClasses
                      .filter(({ isDefault }) => !isDefault)
                      .map(({ issued }) => issued),
                    change: shareClasses
                      .filter(({ isDefault }) => !isDefault)
                      .map(({ issuedChange }) => issuedChange),
                  },
                  [Column.VOTING_RIGHTS]: {
                    change: votingChange,
                    value: voting,
                  },
                  [Column.INVESTMENT]: {
                    change: investmentChange,
                    value: investment,
                  },
                  [Column.AVERAGE_SHARE_PRICE]: {
                    change: avgSharePriceChange,
                    value: avgSharePrice,
                  },
                  [Column.CONVERTED_SHARES]: {
                    change: convertedBasisChange,
                    value: convertedBasis,
                  },
                }}
                key={`${i}_${issued}${type}`}
                modifiedShareClassesIds={modifiedShareClassesIds}
                selectedViewBy={selectedViewBy}
                trClassName="bg-sunbeam-50"
                visibleColumns={visibleColumns}
              />
            );
          },
        )}
    </>
  );
};

export const StakeholdersItemTab: FC<SelectedTabProps> = (props) => {
  const { visibleColumns, selectedViewBy, messagesLocale, viewBy, modifiedShareClassesIds } = props;
  const { data } = ScenarioHooks.useCapTableStakeholders();
  return (
    <>
      {data?.map(
        (
          {
            name,
            avgSharePrice,
            convertedBasis,
            convertedBasisPercentage,
            diluted,
            dilutedPercentage,
            investment,
            issued,
            issuedPercentage,
            shareClasses,
            votingPercentage,
            voting,
            avgSharePriceChange,
            convertedBasisChange,
            convertedBasisPercentageChange,
            dilutedChange,
            dilutedPercentageChange,
            investmentChange,
            issuedChange,
            issuedPercentageChange,
            votingChange,
            votingPercentageChange,
            stakeholder,
          },
          i,
        ) => {
          const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
          return (
            <StakeholderTableItem
              fields={{
                [Column.NAME]: null,
                [Column.STAKEHOLDER_NAME]: { change: null, value: name },
                [Column.TYPE]: {
                  value:
                    messagesLocale[
                      stakeholderTypeTranslation[stakeholder?.type as StakeholderType]
                    ] || 'New Investor',
                  change: null,
                },
                [Column.ISSUED]: {
                  value: issuedPercentage * 100,
                  change: issuedPercentageChange,
                },
                [Column.DILUTED]: {
                  value: dilutedPercentage * 100,
                  change: dilutedPercentageChange,
                },
                [Column.VOTING]: {
                  value: votingPercentage * 100,
                  change: votingPercentageChange,
                },
                [Column.CONVERTED_PERCENTAGE]: {
                  value: convertedBasisPercentage * 100,
                  change: convertedBasisPercentageChange,
                },
                [Column.NO_ISSUED]: {
                  change: issuedChange,
                  value: issued,
                },
                [Column.DILUTED_SHARES]: {
                  change: dilutedChange,
                  value: diluted,
                },
                [Column.COMMON_STOCK]: {
                  change: toNumber(defaultShareClass?.issuedChange),
                  value: toNumber(defaultShareClass?.issued),
                },
                [Column.OTHER_SHARE_CLASSES]: {
                  value: shareClasses
                    .filter(({ isDefault }) => !isDefault)
                    .map(({ issued }) => issued),
                  change: shareClasses
                    .filter(({ isDefault }) => !isDefault)
                    .map(({ issuedChange }) => issuedChange),
                },
                [Column.VOTING_RIGHTS]: {
                  change: votingChange,
                  value: voting,
                },
                [Column.INVESTMENT]: {
                  change: investmentChange,
                  value: investment,
                },
                [Column.AVERAGE_SHARE_PRICE]: {
                  change: avgSharePriceChange,
                  value: avgSharePrice,
                },
                [Column.CONVERTED_SHARES]: {
                  change: convertedBasisChange,
                  value: convertedBasis,
                },
              }}
              key={`${i}_${issued}${name}`}
              modifiedShareClassesIds={modifiedShareClassesIds}
              selectedViewBy={selectedViewBy}
              viewBy={viewBy}
              visibleColumns={visibleColumns}
            />
          );
        },
      )}
      <StakeholderTabPools {...props} />
    </>
  );
};

const StakeholderTabPools: FC<SelectedTabProps> = ({
  visibleColumns,
  viewBy,
  selectedViewBy,
  companyPools,
  scenarioPools,
  shareClasses,
  shareClassesFromCompanyPools,
  shareClassesFromScenarioPools,
  messagesLocale,
  defaultShareClassId,
}) => {
  return (
    <>
      <EquityPoolsRow
        fields={{
          [Column.NAME]: {
            value: messagesLocale['EQUITY_POOLS_GRANTABLE'],
            change: null,
          },
          [Column.STAKEHOLDER_NAME]: {
            value: messagesLocale['EQUITY_POOLS_GRANTABLE'],
            change: null,
          },
          [Column.TYPE]: {
            value:
              selectedViewBy === StakeholdersViewBy.STAKEHOLDERS
                ? ' '
                : messagesLocale['EQUITY_POOLS_GRANTABLE'],
            change: null,
          },
          [Column.NO_ISSUED]: {
            change: null,
            value: calcTotal(companyPools, ({ issued }) => issued.value),
          },
          [Column.DILUTED_SHARES]: {
            change: null,
            value: calcTotal(companyPools, ({ diluted }) => diluted.value),
          },
          [Column.CONVERTED_SHARES]: {
            change: null,
            value: calcTotal(companyPools, ({ convertedBasis }) => convertedBasis.value),
          },
          [Column.COMMON_STOCK]: {
            value: calcTotal(companyPools, ({ defaultShareClass }) => defaultShareClass.value),
            change: null,
          },
          [Column.OTHER_SHARE_CLASSES]: {
            value: shareClasses
              ?.filter(({ id }) => id !== defaultShareClassId)
              ?.map((shareClass) =>
                calcTotal(shareClassesFromCompanyPools, ({ id, value }) =>
                  shareClass.id === id ? value : 0,
                ),
              ),
            change: null,
          },
          [Column.ISSUED]: {
            value: toRound(
              calcTotal(companyPools, ({ issuedPercentage }) => issuedPercentage.value) * 100,
            ),
            change: null,
          },
          [Column.DILUTED]: {
            value: toRound(
              calcTotal(companyPools, ({ dilutedPercentage }) => dilutedPercentage.value) * 100,
            ),
            change: null,
          },
          [Column.CONVERTED_PERCENTAGE]: {
            change: null,
            value: toRound(
              calcTotal(companyPools, ({ convertedBasisPercentage }) =>
                toNumber(convertedBasisPercentage.value),
              ) * 100,
            ),
          },

          [Column.VOTING]: { value: '-', change: null },
          [Column.INVESTMENT]: { value: '-', change: null },
          [Column.AVERAGE_SHARE_PRICE]: { value: '-', change: null },
          [Column.VOTING_RIGHTS]: { value: '-', change: null },
        }}
        items={companyPools}
        selectedViewBy={selectedViewBy}
        tab={CapTableTab.BY_STAKEHOLDER}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
      <EquityPoolsRow
        fields={{
          [Column.NAME]: {
            value: 'Equity Pools Granted',
            change: null,
          },
          [Column.STAKEHOLDER_NAME]: {
            value: 'Equity Pools Granted',
            change: null,
          },
          [Column.TYPE]: {
            value:
              selectedViewBy === StakeholdersViewBy.STAKEHOLDERS ? ' ' : 'Equity Pools Granted',
            change: null,
          },
          [Column.NO_ISSUED]: {
            change: null,
            value: calcTotal(companyPools, ({ unvested }) => unvested?.value || 0),
          },
          [Column.DILUTED_SHARES]: {
            change: null,
            value: 0,
          },
          [Column.CONVERTED_SHARES]: {
            change: null,
            value: 0,
          },
          [Column.COMMON_STOCK]: {
            value: getScenarioPoolUnvestedShareClassTotals(
              companyPools,
              defaultShareClassId,
              defaultShareClassId,
            ),
            change: null,
          },
          [Column.OTHER_SHARE_CLASSES]: {
            value: shareClasses
              ?.filter(({ id }) => id !== defaultShareClassId)
              ?.map((shareClass) =>
                getScenarioPoolUnvestedShareClassTotals(
                  companyPools,
                  defaultShareClassId,
                  shareClass.id,
                ),
              ),
            change: null,
          },
          [Column.ISSUED]: {
            value: toRound(
              calcTotal(companyPools, ({ unvestedPercentage }) => unvestedPercentage?.value || 0) *
                100,
            ),
            change: null,
          },
          [Column.DILUTED]: {
            value: 0,
            change: null,
          },
          [Column.CONVERTED_PERCENTAGE]: {
            change: null,
            value: 0,
          },

          [Column.VOTING]: { value: '-', change: null },
          [Column.INVESTMENT]: { value: '-', change: null },
          [Column.AVERAGE_SHARE_PRICE]: { value: '-', change: null },
          [Column.VOTING_RIGHTS]: { value: '-', change: null },
        }}
        items={companyPools.map((pool) => {
          const unvestedValue = pool?.unvested?.value || 0;
          const unvestedChange = pool?.unvested?.change || 0;
          const shareClassAmounts = mapScenarioPoolShareClassAmounts({
            pool,
            defaultShareClassId,
            poolShareClassId: pool.shareClassId,
            amount: unvestedValue,
            amountChange: unvestedChange,
          });

          return {
            ...pool,
            ...getScenarioGrantedPoolFigures(pool),
            defaultShareClass: shareClassAmounts.defaultShareClass,
            shareClasses: shareClassAmounts.shareClasses,
          };
        })}
        selectedViewBy={selectedViewBy}
        tab={CapTableTab.BY_STAKEHOLDER}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
      {scenarioPools && scenarioPools.length > 0 && (
        <EquityPoolsRow
          fields={{
            [Column.NAME]: {
              value: toUpperCaseWords(messagesLocale['NEW_POOLS']) || '',
              change: null,
            },
            [Column.STAKEHOLDER_NAME]: {
              value: toUpperCaseWords(messagesLocale['NEW_POOLS']) || '',
              change: null,
            },
            [Column.TYPE]: {
              value:
                selectedViewBy === StakeholdersViewBy.STAKEHOLDERS
                  ? ' '
                  : toUpperCaseWords(messagesLocale['NEW_POOLS']) || '',
              change: null,
            },
            [Column.NO_ISSUED]: {
              change: calcTotal(scenarioPools, ({ issued }) => issued.change),
              value: calcTotal(scenarioPools, ({ issued }) => issued.value),
            },
            [Column.DILUTED_SHARES]: {
              change: calcTotal(scenarioPools, ({ diluted }) => diluted.change),
              value: calcTotal(scenarioPools, ({ diluted }) => diluted.value),
            },
            [Column.CONVERTED_SHARES]: {
              change: calcTotal(scenarioPools, ({ convertedBasis }) => convertedBasis.change),
              value: calcTotal(scenarioPools, ({ convertedBasis }) => convertedBasis.value),
            },
            [Column.COMMON_STOCK]: {
              value: calcTotal(scenarioPools, ({ defaultShareClass }) => defaultShareClass.value),
              change: calcTotal(scenarioPools, ({ defaultShareClass }) => defaultShareClass.change),
            },
            [Column.OTHER_SHARE_CLASSES]: {
              value: shareClasses
                ?.filter(({ id }) => id !== defaultShareClassId)
                ?.map((shareClass) =>
                  calcTotal(shareClassesFromScenarioPools, ({ id, value }) =>
                    shareClass.id === id ? value : 0,
                  ),
                ),
              change: shareClasses
                ?.filter(({ id }) => id !== defaultShareClassId)
                ?.map((shareClass) =>
                  calcTotal(shareClassesFromScenarioPools, ({ id, change }) =>
                    shareClass.id === id ? change : 0,
                  ),
                ),
            },
            [Column.ISSUED]: {
              value: toRound(
                calcTotal(scenarioPools, ({ issuedPercentage }) => issuedPercentage.value) * 100,
              ),
              change: calcTotal(scenarioPools, ({ issuedPercentage }) => issuedPercentage.change),
            },
            [Column.DILUTED]: {
              value: toRound(
                calcTotal(scenarioPools, ({ dilutedPercentage }) => dilutedPercentage.value) * 100,
              ),
              change: calcTotal(scenarioPools, ({ dilutedPercentage }) => dilutedPercentage.change),
            },
            [Column.CONVERTED_PERCENTAGE]: {
              change: calcTotal(
                scenarioPools,
                ({ convertedBasisPercentage }) => convertedBasisPercentage.change,
              ),
              value: toRound(
                calcTotal(scenarioPools, ({ convertedBasisPercentage }) =>
                  toNumber(convertedBasisPercentage.value),
                ) * 100,
              ),
            },

            [Column.VOTING]: { value: '-', change: null },
            [Column.INVESTMENT]: { value: '-', change: null },
            [Column.AVERAGE_SHARE_PRICE]: { value: '-', change: null },
            [Column.VOTING_RIGHTS]: { value: '-', change: null },
          }}
          items={scenarioPools}
          selectedViewBy={selectedViewBy}
          tab={CapTableTab.BY_STAKEHOLDER}
          trClassName="bg-sunbeam-50"
          viewBy={viewBy}
          visibleColumns={visibleColumns}
        />
      )}
    </>
  );
};
